import {
  Component,
  Host,
  h,
  State,
  Prop,
  Fragment,
  Listen,
} from '@stencil/core';
import { getClosingHourFromBank } from '../../../../global/utility/get-closing-hour-from-bank';
import store from '../../../../global/store';
import { tracker } from '../../../../global/tracker';

@Component({
  tag: 'ui-bank-search-bank-overlay-details',
  styleUrl: 'ui-bank-search-bank-overlay-details.scss',
  shadow: false,
})
export class UiBankSearchBankOverlayDetails {
  @Prop() hasForm = false;

  @State() isBankInfoActive = false;

  @State() isOpeningTimeActive = true;

  onSelectedBankChange() {
    if (!store.state.selectedBank.openingTime.mon?.[0]) {
      this.isOpeningTimeActive = false;
    }
  }

  componentWillLoad() {
    store.onChange('selectedBank', () => {
      this.onSelectedBankChange();
    });
  }

  backButton: HTMLElement;

  @Listen('overlayVisibilityChange', { target: 'window' })
  handleOverlayVisibilityChange(event: CustomEvent<{ isVisible: boolean }>) {
    if (event.detail.isVisible && this.backButton) {
      setTimeout(() => {
        this.backButton.focus(); // This moves the focus inside the overlay
      }, 100);
    }
  }

  @Listen('sd-change')
  handleRadioButtonClick(event: Event) {
    if (
      (event.target as HTMLSelectElement).name !== 'bank-details-mobile-trigger'
    ) {
      return;
    }

    this.isOpeningTimeActive =
      (event.target as HTMLSelectElement).value === 'opening';
  }

  generateOpeningTimeForDay(label: string, openingTime: string[][]) {
    return (
      <tr>
        <td>{label}</td>
        {openingTime && openingTime[0] ? (
          ''
        ) : (
          <td>
            {(window as any).ui.services?.i18n?.translate('bankSearch')
              .openingHoursClosed || 'Geschlossen'}
          </td>
        )}
        {openingTime?.[0] ? (
          <td>
            {openingTime[0][0]} - {openingTime[0][1]}
          </td>
        ) : (
          ''
        )}
        {openingTime?.[1] ? (
          <td>
            {openingTime[1][0]} - {openingTime[1][1]}
          </td>
        ) : (
          ''
        )}
      </tr>
    );
  }

  private linkButtonClickHandler = {
    bankDetailInfo: () => {
      this.isBankInfoActive = !this.isBankInfoActive;
    },
    back: () => {
      store.state.isOverlayVisible = false;
      tracker.trackOverlayBackLink();
    },
    directions: () => tracker.trackBankDirectionsLink(),
    website: () => tracker.trackBankWebsiteLink(),
    mail: () => tracker.trackBankMailLink(),
    phone: () => tracker.trackBankPhoneLink(),
    openingTimeButton: () => {
      this.isOpeningTimeActive = true;
      tracker.trackBankOpeningHoursButton();
    },
    contactButton: () => {
      this.isOpeningTimeActive = false;
      tracker.trackBankContactButton();
    },
  };

  getDirectionsLink() {
    const { selectedBank, selectedPlaceLocation, selectedPlaceName } =
      store.state;

    const startAddress =
      selectedPlaceLocation.lat() &&
      selectedPlaceLocation.lng() &&
      selectedPlaceName === 'Aktueller Standort'
        ? `${selectedPlaceLocation.lat()},${selectedPlaceLocation.lng()}`
        : null;
    const destinationAddress = `${selectedBank.coords.latitude},${selectedBank.coords.longitude}`;

    const link = startAddress
      ? `https://www.google.de/maps?saddr=${startAddress}&daddr=${destinationAddress}`
      : `https://www.google.de/maps?daddr=${destinationAddress}`;
    return link;
  }

  render() {
    const bank = store.state.selectedBank;
    return (
      <Host>
        <div class="bank-details">
          <div class="bank-details__back-button">
            <sd-button
              ref={(el) => {
                this.backButton = el;
              }}
              variant="tertiary"
              onClick={() => this.linkButtonClickHandler.back()}
            >
              {(window as any).ui.services?.i18n?.translate('bankSearch')
                .backToBranchSelection || 'Zurück zur Filialauswahl'}
              <sd-icon
                name="union-investment/system/chevron-left"
                slot="icon-left"
              />
            </sd-button>
          </div>
          <div class="bank-details__bank-name">
            <img
              alt="Logo der Bank"
              src={
                bank.institute.map_marker_logo_url ||
                `${store.state.assetsUrl}/bank-fallback.png`
              }
            ></img>
            <h2 class="sd-headline sd-headline--size-base">{bank.name}</h2>
          </div>
          {getClosingHourFromBank(bank) && (
            <p class="sd-paragraph sd-paragraph--size-sm bank-details__closing-hour">
              {`${(window as any).ui.services?.i18n?.translate('bankSearch').openingHoursOpen || 'Geöffnet'}: ${(window as any).ui.services?.i18n?.translate('bankSearch').openingHoursToday || 'Heute'} ${(window as any).ui.services?.i18n?.translate('bankSearch').openingHoursTo || 'bis'} ${getClosingHourFromBank(bank)} ${(window as any).ui.services?.i18n?.translate('bankSearch').clock || 'Uhr'}`}
            </p>
          )}
          <div
            class={{
              'bank-details__info': true,
              'bank-details__info--active':
                !this.hasForm || this.isBankInfoActive,
            }}
          >
            <div class="bank-details__directions">
              <sd-link
                onClick={() => this.linkButtonClickHandler.directions()}
                size="sm"
                target="_blank"
                href={this.getDirectionsLink()}
              >
                <sd-icon
                  slot="icon-left"
                  name="union-investment/system/position"
                />
                {(window as any).ui.services?.i18n?.translate('bankSearch')
                  .routePlanner || 'Routenplaner'}
              </sd-link>
            </div>
            <div class="bank-details__mobile-trigger">
              <sd-radio-group
                name="bank-details-mobile-trigger"
                size="sm"
                value="contact"
              >
                <sd-radio-button value="contact">
                  {(window as any).ui.services?.i18n?.translate('bankSearch')
                    .contact || 'Kontakt'}
                </sd-radio-button>
                <sd-radio-button value="opening">
                  {(window as any).ui.services?.i18n?.translate('bankSearch')
                    .openingHours || 'Öffnungszeiten'}
                </sd-radio-button>
              </sd-radio-group>
            </div>
            <div class="bank-details__contact">
              <div class="bank-details__contact-section">
                <h3 class="bank-details__contact-section--title">
                  <strong>
                    {(window as any).ui.services?.i18n?.translate('bankSearch')
                      .contact || 'Kontakt'}
                  </strong>
                </h3>
                <div
                  class={{
                    'bank-details__contact-section--content': true,
                    'bank-details__contact-section--content--hide':
                      this.isOpeningTimeActive,
                  }}
                >
                  <div>
                    <p class="sd-paragraph sd-paragraph--size-sm">
                      {bank.street}
                      <br></br>
                      {bank.zipCode} {bank.city}
                    </p>
                  </div>
                  <p class="sd-paragraph sd-paragraph--size-sm">
                    {bank.phoneArea && bank.phoneNumber !== 'null' && (
                      <Fragment>
                        <sd-link
                          href={`tel:${bank.phoneArea}${bank.phoneNumber}`}
                          onClick={() => this.linkButtonClickHandler.phone()}
                        >
                          <sd-icon
                            slot="icon-left"
                            name="union-investment/system/phone"
                          />
                          {bank.phoneArea} {bank.phoneNumber}
                        </sd-link>
                        <br />
                      </Fragment>
                    )}
                    {bank.email && (
                      <Fragment>
                        <sd-link
                          href={`mailto:${bank.email}`}
                          onClick={() => this.linkButtonClickHandler.mail()}
                        >
                          <sd-icon
                            slot="icon-left"
                            name="union-investment/system/e-mail"
                          />
                          {bank.email}
                        </sd-link>
                        <br />
                      </Fragment>
                    )}
                    {bank.url && (
                      <sd-link
                        target="_blank"
                        href={bank.url}
                        onClick={() => this.linkButtonClickHandler.website()}
                      >
                        <sd-icon
                          slot="icon-left"
                          name="union-investment/system/globe"
                        />
                        {bank.url}
                      </sd-link>
                    )}
                  </p>
                  <p class="sd-paragraph sd-paragraph--size-sm">
                    <strong>
                      {(window as any).ui.services?.i18n?.translate(
                        'bankSearch'
                      ).bankCode || 'BLZ'}
                      :
                    </strong>{' '}
                    {bank.bankCode}
                    <br></br>
                    <strong>
                      {(window as any).ui.services?.i18n?.translate(
                        'bankSearch'
                      ).bic || 'BIC'}
                      :
                    </strong>{' '}
                    {bank.bic}
                  </p>
                </div>
              </div>
            </div>
            <div class="bank-details__opening-hours">
              <h3 class="bank-details__opening-hours--title">
                <strong>
                  {(window as any).ui.services?.i18n?.translate('bankSearch')
                    .openingHours || 'Öffnungszeiten'}
                </strong>
              </h3>
              <div
                class={{
                  'bank-details__opening-hours--content': true,
                  'bank-details__opening-hours--content--hide':
                    !this.isOpeningTimeActive,
                }}
              >
                {bank.openingTime.mon?.[0] ? (
                  <p class="sd-paragraph sd-paragraph--size-sm">
                    <table>
                      {this.generateOpeningTimeForDay(
                        'Mo',
                        bank.openingTime.mon
                      )}
                      {this.generateOpeningTimeForDay(
                        'Di',
                        bank.openingTime.tue
                      )}
                      {this.generateOpeningTimeForDay(
                        'Mi',
                        bank.openingTime.wed
                      )}
                      {this.generateOpeningTimeForDay(
                        'Do',
                        bank.openingTime.thu
                      )}
                      {this.generateOpeningTimeForDay(
                        'Fr',
                        bank.openingTime.fri
                      )}
                      {this.generateOpeningTimeForDay(
                        'Sa',
                        bank.openingTime.sat
                      )}
                      {this.generateOpeningTimeForDay(
                        'So',
                        bank.openingTime.sun
                      )}
                    </table>
                  </p>
                ) : (
                  <p class="sd-paragraph sd-paragraph--size-sm">
                    {(window as any).ui.services?.i18n?.translate('bankSearch')
                      .currentlyNoInfoAvailable ||
                      'Momentan keine Informationen verfügbar.'}
                  </p>
                )}
              </div>
            </div>
          </div>
          {this.hasForm && (
            <sd-button
              variant="tertiary"
              class="bank-details__mobile-toggle"
              onClick={() => this.linkButtonClickHandler.bankDetailInfo()}
            >
              <sd-icon
                slot="icon-left"
                name={`union-investment/system/chevron-${this.isBankInfoActive ? 'top' : 'bottom'}`}
              />
              {this.isBankInfoActive
                ? 'Bankeninformationen ausblenden'
                : 'Bankeninformationen einblenden'}
            </sd-button>
          )}
        </div>
      </Host>
    );
  }
}
